import { get } from "lodash-es";
import { MESApiService, type spParameter } from "../../../../../services";
import { TagFromTree, View } from "../models/TrendingModelsV2";

const getIhPath = () =>
	localStorage.getItem("ihPath") || "[mssql2022].[IH_100020].[ih]";

//tags
export const getTagsTree = async (signal?: AbortSignal) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	const resp = await apiService.callV2(
		getIhPath() + ".[GetTagsTree]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as TagFromTree[];
	} else {
		throw new Error(resp.message || "Error fetching tags tree");
	}
};

export const deleteTagFolder = async (TagFolderId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "TagFolderId", value: TagFolderId },
	];
	const backendOffset = 1000000;

	if (TagFolderId && TagFolderId > backendOffset) {
		TagFolderId = TagFolderId - backendOffset;
	}

	const resp = await apiService.callV2(
		getIhPath() + ".[DeleteTagFolder]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when delete tag folder");
	}
};

export const upsertTagFolder = async ({
	tagFolderId,
	parentTagFolderId,
	tagFolderName,
	isActive,
}: {
	tagFolderId: number | null;
	parentTagFolderId: number | null;
	tagFolderName: string;
	isActive: boolean;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "TagFolderId", value: tagFolderId },
		{ name: "ParentTagFolderId", value: parentTagFolderId },
		{ name: "TagFolderName", value: tagFolderName },
		{ name: "IsActive", value: isActive },
	];
	const backendOffset = 1000000;

	if (tagFolderId && tagFolderId > backendOffset) {
		tagFolderId = tagFolderId - backendOffset;
	}
	if (parentTagFolderId && parentTagFolderId > backendOffset) {
		parentTagFolderId = parentTagFolderId - backendOffset;
	}

	const resp = await apiService.callV2(
		getIhPath() + ".[UpsertTagFolder]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when upsert tag folder");
	}
};

export const changeTagFolderToTag = async ({
	tagId,
	tagFolderId,
}: {
	tagId: number | null;
	tagFolderId: number | null;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "TagId", value: tagId },
		{ name: "TagFolderId", value: tagFolderId },
	];
	const backendOffset = 1000000;

	if (tagFolderId && tagFolderId > backendOffset) {
		tagFolderId = tagFolderId - backendOffset;
	}

	const resp = await apiService.callV2(
		getIhPath() + ".[ChangeTagFolderToTag]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when change tag folder tag");
	}
};
/* */

export const getSeriesData = async (
	TagIds: string,
	Start: number | null,
	End: number | null,
	SampleCount: number,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	parameters.push({ name: "TagIds", value: TagIds });
	parameters.push({ name: "Start", value: Start });
	parameters.push({ name: "End", value: End });
	parameters.push({ name: "SampleCount", value: SampleCount });

	const resp = await apiService.callV2(
		getIhPath() + ".[GetSeriesData]",
		parameters,
	);
	return resp;
};

export const saveTagDefaults = async ({
	tagId,
	alias,
	color,
	minScale,
	maxScale,
	unit,
}: {
	tagId: number;
	alias: string;
	color: string;
	minScale: number;
	maxScale: number;
	unit: string;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "TagId", value: tagId },
		{ name: "Alias", value: alias },
		{ name: "Color", value: color },
		{ name: "MinScale", value: minScale },
		{ name: "MaxScale", value: maxScale },
		{ name: "Unit", value: unit },
	];

	const resp = await apiService.callV2(
		getIhPath() + ".[SaveTagDefaults]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when save tag defaults");
	}
};

//views

export const getViews = async (
	params: { Search: string | null },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "Search", value: params.Search }];

	const resp = await apiService.callV2(
		getIhPath() + ".[GetViews]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as View[];
	} else {
		throw new Error(resp.message || "Error fetching views");
	}
};

export const upsertView = async ({
	viewId,
	viewName,
	isPublic,
}: {
	viewId: number | null;
	viewName: string;
	isPublic: boolean;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "ViewId", value: viewId },
		{ name: "ViewName", value: viewName },
		{ name: "isPublic", value: isPublic },
	];

	const resp = await apiService.callV2(
		getIhPath() + ".[UpsertView]",
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Failed to save view");
	}
};

export const deleteView = async (ViewId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "ViewId", value: ViewId }];

	const resp = await apiService.callV2(
		getIhPath() + ".[DeleteView]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when delete view");
	}
};

//viewTags
export const getViewTags = async (
	params: { viewId: number },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [{ name: "ViewId", value: params.viewId }];

	const resp = await apiService.callV2(
		getIhPath() + ".[GetViewTags]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as any[];
	} else {
		throw new Error(resp.message || "Error fetching view tags");
	}
};

export const upsertViewTag = async ({
	viewId,
	tagId,
	color,
	minScale,
	maxScale,
	isVisible,
	isAutoScale,
	unit,
	order,
	alias,
	tagType,
	sourceTagId,
	bitIndex,
}: {
	viewId: number;
	tagId: number;
	color: string;
	minScale: number;
	maxScale: number;
	isVisible: boolean;
	isAutoScale: boolean;
	unit: string;
	order?: number;
	alias?: string;
	tagType?: string;
	sourceTagId?: number;
	bitIndex?: number;
}) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "ViewId", value: viewId },
		{ name: "TagId", value: tagId },
		{ name: "Color", value: color },
		{ name: "MinScale", value: minScale },
		{ name: "MaxScale", value: maxScale },
		{ name: "IsVisible", value: isVisible },
		{ name: "IsAutoScale", value: isAutoScale },
		{ name: "Unit", value: unit },
	];

	const resp = await apiService.callV2(
		getIhPath() + ".[UpsertViewTag]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when upsert view tag");
	}
};

export const deleteViewTag = async (ViewId: number, TagId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	parameters.push({ name: "ViewId", value: ViewId });
	parameters.push({ name: "TagId", value: TagId });

	const resp = await apiService.callV2(
		getIhPath() + ".[DeleteViewTag]",
		parameters,
	);
	return resp;
};
export const deleteAllViewTagsFromView = async (ViewId: number) => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [];

	parameters.push({ name: "ViewId", value: ViewId });

	const resp = await apiService.callV2(
		getIhPath() + ".[DeleteAllViewTagsFromView]",
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error when delete all view tags");
	}
};
